import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@powersync/react';
import { useApp, useClinicCtx } from '@/app/state';
import { Button, Card, Field, Input, Modal, Pill } from '@/components/ui';
import { createSession, todayLocal } from '@/features/queue/repo';
import { materializeTodaySessions } from '@/features/queue/schedule';
import { newId, nowIso } from '@/lib/db/db';
import type { DoctorRow, SessionRow } from '@/lib/db/schema';
import { useNavigate } from 'react-router';
import { getAutoPrint, getDeviceMode, setAutoPrint, setDeviceMode, type DeviceMode } from '@/app/device';
import { env } from '@/lib/env';
import { StaffCard } from '@/features/staff/StaffCard';

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const { clinic, session } = useApp();
  const { data: doctors } = useQuery<DoctorRow>('select * from doctors where clinic_id = ? and active = 1 order by name_en', [ctx.clinicId]);
  const { data: sessions } = useQuery<SessionRow & { doctor_name_en: string | null; doctor_name_my: string | null }>(`select s.*, d.name_en as doctor_name_en, d.name_my as doctor_name_my from sessions s join doctors d on d.id = s.doctor_id where s.clinic_id = ? and s.session_date >= date('now','localtime') order by s.session_date, s.start_time`, [ctx.clinicId]);
  const nav = useNavigate();
  const { data: templates } = useQuery<{ id: string; doctor_id: string; weekday: number; start_time: string; end_time: string; room: string | null }>('select id, doctor_id, weekday, start_time, end_time, room from session_templates where clinic_id = ? and active = 1 order by doctor_id, weekday, start_time', [ctx.clinicId]);
  const [addTpl, setAddTpl] = useState<string | null>(null); // doctor id
  const [tpl, setTpl] = useState({ days: [1, 2, 3, 4, 5] as number[], start_time: '17:00', end_time: '20:00', room: '' });
  const [mode, setMode] = useState<DeviceMode>(getDeviceMode() ?? 'full');
  const [autoPrint, setPrint] = useState(getAutoPrint());
  const days = t('timetable.days', { returnObjects: true }) as string[];
  const [addDoc, setAddDoc] = useState(false);
  const [addSess, setAddSess] = useState(false);
  const [doc, setDoc] = useState({ name_my: '', name_en: '', specialty: '', mmc_reg_no: '', default_fee: '5000', revenue_share_pct: '60' });
  const [sess, setSess] = useState({ doctor_id: '', session_date: todayLocal(), start_time: '17:00', end_time: '20:00', room: '' });

  async function saveDoctor(e: React.FormEvent) {
    e.preventDefault();
    const ts = nowIso();
    await ctx.db.execute(`insert into doctors (id, clinic_id, name_my, name_en, specialty, mmc_reg_no, default_fee, revenue_share_pct, active, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?)`,
      [newId(), ctx.clinicId, doc.name_my || null, doc.name_en || null, doc.specialty || null, doc.mmc_reg_no || null, Number(doc.default_fee) || 0, Number(doc.revenue_share_pct) || 0, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    setAddDoc(false);
  }
  async function saveTimetable(e: React.FormEvent) {
    e.preventDefault();
    if (!addTpl) return;
    const ts = nowIso();
    for (const d of tpl.days) {
      await ctx.db.execute(`insert into session_templates (id, clinic_id, doctor_id, weekday, start_time, end_time, room, active, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?)`,
        [newId(), ctx.clinicId, addTpl, d, tpl.start_time, tpl.end_time, tpl.room || null, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
    await materializeTodaySessions(ctx.db, ctx.clinicId, ctx.userId, ctx.deviceId);
    setAddTpl(null);
  }
  async function removeTemplate(id: string) {
    await ctx.db.execute('update session_templates set active = 0, updated_at = ?, updated_by = ? where id = ?', [nowIso(), ctx.userId, id]);
  }
  function saveDevice() {
    setDeviceMode(mode); setAutoPrint(autoPrint);
    nav('/', { replace: true });
  }

  async function saveSession(e: React.FormEvent) {
    e.preventDefault();
    await createSession(ctx, { ...sess, doctor_id: sess.doctor_id || (doctors?.[0]?.id ?? ''), room: sess.room || null, open: sess.session_date === todayLocal() });
    setAddSess(false);
  }

  return (
    <div className="grid gap-5">
      <Card>
        <h2 className="mb-2 text-lg font-bold">{t('settings.clinic')}</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted">{t('clinic.name')}</dt><dd>{clinic?.name} {clinic?.name_my && <span className="font-my text-ink-2">· {clinic.name_my}</span>}</dd>
          <dt className="text-muted">{t('settings.devices')}</dt><dd>{session?.deviceId?.slice(0, 8)} · {t('queue.token')} prefix "{session?.tokenPrefix || '—'}"</dd>
          <dt className="text-muted">{t('settings.keys')}</dt><dd><Pill tone="accent">AES-256-GCM · v1</Pill> <span className="text-ink-2">{my ? 'ဒီစက်တွင် ဖွင့်ထားသည်' : 'unlocked on this device'}</span></dd>
        </dl>
      </Card>

      <Card>
        <h2 className="mb-2 text-lg font-bold">{t('device.mode')}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <select className="rounded-lg border border-rule bg-surface px-3 py-2.5" value={mode} onChange={(e) => setMode(e.target.value as DeviceMode)}>
            {(['reception', 'doctor', 'pharmacy', 'board', 'full'] as DeviceMode[]).map((m) => <option key={m} value={m}>{t(`device.${m}`)}</option>)}
          </select>
          <label className="flex items-center gap-2"><input type="checkbox" className="h-5 w-5" checked={autoPrint} onChange={(e) => setPrint(e.target.checked)} />{t('device.autoPrint')}</label>
          <Button variant="primary" onClick={saveDevice}>{t('common.save')}</Button>
          <span className="text-sm text-muted">{t('update.version')} {env.version}</span>
        </div>
      </Card>

      <StaffCard />

      <Card>
        <div className="mb-2 flex items-center justify-between"><h2 className="text-lg font-bold">{t('settings.doctors')}</h2><Button onClick={() => setAddDoc(true)}>＋ {t('doctor.new')}</Button></div>
        <ul className="divide-y divide-rule text-sm">
          {(doctors ?? []).map((d) => {
            const mine = (templates ?? []).filter((x) => x.doctor_id === d.id);
            return (
              <li key={d.id} className="py-2">
                <div className="flex flex-wrap justify-between gap-2"><span><b>{my ? (d.name_my ?? d.name_en) : (d.name_en ?? d.name_my)}</b> <span className="text-ink-2">{d.specialty}</span></span><span className="text-ink-2">{d.default_fee?.toLocaleString()} Ks · {d.revenue_share_pct}%</span></div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="text-muted">{t('timetable.title')}:</span>
                  {mine.length === 0 && <span className="text-muted">{t('common.none')}</span>}
                  {mine.map((x) => <Pill key={x.id} tone="accent">{days[x.weekday]} {x.start_time}–{x.end_time}{x.room ? ` · ${x.room}` : ''} <button className="ml-1 opacity-70 hover:opacity-100" onClick={() => removeTemplate(x.id)} aria-label={t('timetable.remove')}>✕</button></Pill>)}
                  <Button className="min-h-7 px-2 py-0.5 text-xs" onClick={() => setAddTpl(d.id)}>＋ {t('timetable.add')}</Button>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card>
        <div className="mb-2 flex items-center justify-between"><h2 className="text-lg font-bold">{t('settings.sessions')}</h2><Button onClick={() => setAddSess(true)} disabled={(doctors ?? []).length === 0}>＋ {t('session.new')}</Button></div>
        <ul className="divide-y divide-rule text-sm">
          {(sessions ?? []).map((s) => <li key={s.id} className="flex flex-wrap justify-between gap-2 py-2"><span>{s.session_date} · {s.start_time}–{s.end_time} · <b>{my ? (s.doctor_name_my ?? s.doctor_name_en) : (s.doctor_name_en ?? s.doctor_name_my)}</b></span><Pill tone={s.status === 'open' ? 'accent' : 'neutral'}>{t(`session.status.${s.status}`)}</Pill></li>)}
        </ul>
      </Card>

      <Modal open={addDoc} onClose={() => setAddDoc(false)} title={t('doctor.new')}>
        <form onSubmit={saveDoctor} className="grid gap-3">
          <Field label={t('patient.nameMy')}><Input className="font-my" value={doc.name_my} onChange={(e) => setDoc({ ...doc, name_my: e.target.value })} /></Field>
          <Field label={t('patient.nameEn')}><Input value={doc.name_en} onChange={(e) => setDoc({ ...doc, name_en: e.target.value })} /></Field>
          <Field label={t('doctor.specialty')}><Input value={doc.specialty} onChange={(e) => setDoc({ ...doc, specialty: e.target.value })} /></Field>
          <Field label={t('doctor.regNo')}><Input value={doc.mmc_reg_no} onChange={(e) => setDoc({ ...doc, mmc_reg_no: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('doctor.fee')}><Input type="number" inputMode="numeric" value={doc.default_fee} onChange={(e) => setDoc({ ...doc, default_fee: e.target.value })} /></Field>
            <Field label={t('doctor.share')}><Input type="number" inputMode="numeric" min={0} max={100} value={doc.revenue_share_pct} onChange={(e) => setDoc({ ...doc, revenue_share_pct: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-2"><Button type="button" onClick={() => setAddDoc(false)}>{t('common.cancel')}</Button><Button type="submit" variant="primary" disabled={!doc.name_en && !doc.name_my}>{t('common.save')}</Button></div>
        </form>
      </Modal>

      <Modal open={!!addTpl} onClose={() => setAddTpl(null)} title={t('timetable.add')}>
        <form onSubmit={saveTimetable} className="grid gap-3">
          <div className="flex flex-wrap gap-2">
            {days.map((label, i) => (
              <button type="button" key={i} onClick={() => setTpl({ ...tpl, days: tpl.days.includes(i) ? tpl.days.filter((x) => x !== i) : [...tpl.days, i] })}
                className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${tpl.days.includes(i) ? 'border-accent bg-accent-soft text-accent-ink' : 'border-rule bg-surface text-ink-2'}`}>{label}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('session.start')}><Input type="time" value={tpl.start_time} onChange={(e) => setTpl({ ...tpl, start_time: e.target.value })} /></Field>
            <Field label={t('session.end')}><Input type="time" value={tpl.end_time} onChange={(e) => setTpl({ ...tpl, end_time: e.target.value })} /></Field>
          </div>
          <Field label={t('queue.room')}><Input value={tpl.room} onChange={(e) => setTpl({ ...tpl, room: e.target.value })} /></Field>
          <p className="text-sm text-muted">{t('timetable.none')}</p>
          <div className="flex justify-end gap-2"><Button type="button" onClick={() => setAddTpl(null)}>{t('common.cancel')}</Button><Button type="submit" variant="primary" disabled={tpl.days.length === 0}>{t('common.save')}</Button></div>
        </form>
      </Modal>

      <Modal open={addSess} onClose={() => setAddSess(false)} title={t('session.new')}>
        <form onSubmit={saveSession} className="grid gap-3">
          <Field label={t('session.doctor')}>
            <select className="w-full rounded-lg border border-rule bg-surface px-3 py-2.5" value={sess.doctor_id} onChange={(e) => setSess({ ...sess, doctor_id: e.target.value })}>
              {(doctors ?? []).map((d) => <option key={d.id} value={d.id}>{my ? (d.name_my ?? d.name_en) : (d.name_en ?? d.name_my)}</option>)}
            </select>
          </Field>
          <Field label={t('session.date')}><Input type="date" value={sess.session_date} onChange={(e) => setSess({ ...sess, session_date: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('session.start')}><Input type="time" value={sess.start_time} onChange={(e) => setSess({ ...sess, start_time: e.target.value })} /></Field>
            <Field label={t('session.end')}><Input type="time" value={sess.end_time} onChange={(e) => setSess({ ...sess, end_time: e.target.value })} /></Field>
          </div>
          <Field label={t('queue.room')}><Input value={sess.room} onChange={(e) => setSess({ ...sess, room: e.target.value })} /></Field>
          <div className="flex justify-end gap-2"><Button type="button" onClick={() => setAddSess(false)}>{t('common.cancel')}</Button><Button type="submit" variant="primary">{t('common.save')}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
